/**
 * Demo 4 – Erfassungs-Flow Builder (Folien-Muster)
 *
 * Umsetzung des abgestimmten Konzepts in docs/CAPTURE-FLOW-BUILDER-KONZEPT.md:
 * - Filmstreifen oben: Reihenfolge der Schritte, Drag & Drop mit Sperren
 * - Bühne: der ausgewählte Schritt in Originalgröße im FlWizard-Look, editierbar
 * - Probelauf: den Flow als Vorarbeiter durchspielen (inkl. Pflicht-Stufen
 *   und bedingter Pflicht "Kunde Pflicht, wenn Wäscheklasse = X", Meldung #13)
 * - Entwurf / Freigeben mit automatischen Prüfungen + ein Rückgängig-Stand
 *
 * READ-ONLY: Keine Backend-Writes, nur lokaler Browser-State.
 * Stammdaten (Strecken, Wäscheklassen, Artikelgruppen, Kunden) kommen live aus der API.
 */

import { useState, useEffect, useMemo, useCallback, DragEvent } from 'react';
import {
  GitBranch, Route, Layers, Package, Users, Scale, Search,
  Play, X, Check, AlertTriangle, RotateCcw, ShieldCheck, Grid3x3,
  Link as LinkIcon, Loader2, Power, ChevronLeft,
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Stepper } from '../../components/Stepper';

// =============================================================================
// TYPES
// =============================================================================

interface SubLine { id: string; name: string }
interface ApiLine { id: string; name: string; subLines?: SubLine[] }
interface LaundryClassEntity { id: string; code: string; name: string }
interface ArticleGroupEntity { id: string; code: string; name: string }
interface Customer { id: string; name: string; shortName: string | null }

type StepKey = 'line' | 'subline' | 'laundryClass' | 'articleGroup' | 'customer' | 'weight';
type Requirement = 'OFF' | 'SOFT' | 'HARD';

interface StepRule {
  key: StepKey;
  enabled: boolean;
  requirement: Requirement;
  /** Bedingte Pflicht, genau eine flache Form: "Pflicht, wenn Wäscheklasse = valueId" */
  requiredIf?: { stepKey: 'laundryClass'; valueId: string } | null;
}

interface FlowDraft {
  lineId: string | null;
  steps: StepRule[];
  allowedSubLineIds: string[];
  allowedLcIds: string[];
  /** Wäscheklasse -> erlaubte Artikelgruppen (die Matrix) */
  lcAgMatrix: Record<string, string[]>;
}

interface ActiveFlow {
  flow: FlowDraft;
  activatedAt: string; // Anzeige, lokal
}

// =============================================================================
// KONSTANTEN
// =============================================================================

const STEP_META: Record<StepKey, { label: string; icon: React.ElementType }> = {
  line: { label: 'Strecke', icon: GitBranch },
  subline: { label: 'Substrecke', icon: Route },
  laundryClass: { label: 'Wäscheklasse', icon: Layers },
  articleGroup: { label: 'Artikelgruppe', icon: Package },
  customer: { label: 'Kunde', icon: Users },
  weight: { label: 'Gewicht', icon: Scale },
};

const DEFAULT_STEPS: StepRule[] = [
  { key: 'line', enabled: true, requirement: 'HARD' },
  { key: 'subline', enabled: false, requirement: 'OFF' },
  { key: 'laundryClass', enabled: true, requirement: 'HARD' },
  { key: 'articleGroup', enabled: true, requirement: 'SOFT' },
  { key: 'customer', enabled: true, requirement: 'SOFT', requiredIf: null },
  { key: 'weight', enabled: true, requirement: 'HARD' },
];

const EMPTY_DRAFT: FlowDraft = {
  lineId: null,
  steps: DEFAULT_STEPS,
  allowedSubLineIds: [],
  allowedLcIds: [],
  lcAgMatrix: {},
};

/** Schritte, die nie abgeschaltet oder verschoben werden können */
const FIXED_FIRST: StepKey = 'line';

const deepCopy = <T,>(v: T): T => JSON.parse(JSON.stringify(v));
const flowsEqual = (a: FlowDraft, b: FlowDraft) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Reihenfolge-Sperren (Konzept, Abschnitt 2):
 * - 'line' ist immer Schritt 1
 * - 'subline' direkt hinter 'line'
 * - 'articleGroup' nie vor 'laundryClass'
 * Liefert null bei erlaubter Reihenfolge, sonst die Begründung.
 */
function orderViolation(steps: StepRule[]): string | null {
  const order = steps.map((s) => s.key);
  if (order[0] !== FIXED_FIRST) return 'Die Strecke bleibt immer Schritt 1.';
  const subIdx = order.indexOf('subline');
  if (subIdx > 1) return 'Die Substrecke gehört direkt hinter die Strecke.';
  const lcIdx = order.indexOf('laundryClass');
  const agIdx = order.indexOf('articleGroup');
  if (agIdx >= 0 && lcIdx >= 0 && agIdx < lcIdx) {
    return 'Artikelgruppen hängen an der Wäscheklasse — der Schritt kann nicht davor liegen.';
  }
  return null;
}

// =============================================================================
// HAUPTSEITE
// =============================================================================

export default function CaptureFlowBuilderDemo4Page() {
  // Stammdaten
  const [lines, setLines] = useState<ApiLine[]>([]);
  const [laundryClasses, setLaundryClasses] = useState<LaundryClassEntity[]>([]);
  const [articleGroups, setArticleGroups] = useState<ArticleGroupEntity[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);

  // Entwurf / Aktiv / Rückgängig
  const [draft, setDraft] = useState<FlowDraft>(EMPTY_DRAFT);
  const [active, setActive] = useState<ActiveFlow | null>(null);
  const [previous, setPrevious] = useState<ActiveFlow | null>(null);

  // UI-Zustand
  const [selectedStep, setSelectedStep] = useState<StepKey>('line');
  const [dragKey, setDragKey] = useState<StepKey | null>(null);
  const [dropHint, setDropHint] = useState<string | null>(null);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [activationErrors, setActivationErrors] = useState<string[] | null>(null);
  const [activationOk, setActivationOk] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Stammdaten laden (wie Demo 3, plus Kunden für die Bühne)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [lRes, lcRes, agRes, cRes] = await Promise.all([
          fetch('/api/data/lines', { credentials: 'include' }),
          fetch('/api/data/laundry-classes', { credentials: 'include' }),
          fetch('/api/data/article-groups', { credentials: 'include' }),
          fetch('/api/data/customers', { credentials: 'include' }),
        ]);
        if (lRes.ok) setLines(await lRes.json());
        if (lcRes.ok) setLaundryClasses(await lcRes.json());
        if (agRes.ok) setArticleGroups(await agRes.json());
        if (cRes.ok) setCustomers(await cRes.json());
      } catch (e) {
        console.error('Fehler beim Laden der Stammdaten:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---------------------------------------------------------------------------
  // Abgeleitetes
  // ---------------------------------------------------------------------------
  const selectedLine = lines.find((l) => l.id === draft.lineId);
  const subLines = useMemo(() => selectedLine?.subLines ?? [], [selectedLine]);
  const enabledSteps = draft.steps.filter((s) => s.enabled);
  const draftDirty = active ? !flowsEqual(draft, active.flow) : true;
  const stepRule = (key: StepKey) => draft.steps.find((s) => s.key === key)!;

  const allowedLcs = laundryClasses.filter((lc) => draft.allowedLcIds.includes(lc.id));

  // ---------------------------------------------------------------------------
  // Aktionen: Entwurf bearbeiten
  // ---------------------------------------------------------------------------
  const patchStep = useCallback((key: StepKey, patch: Partial<StepRule>) => {
    setDraft((prev) => ({
      ...prev,
      steps: prev.steps.map((s) => (s.key === key ? { ...s, ...patch } : s)),
    }));
  }, []);

  const moveStep = useCallback((key: StepKey, targetIdx: number) => {
    setDraft((prev) => {
      const steps = [...prev.steps];
      const fromIdx = steps.findIndex((s) => s.key === key);
      if (fromIdx < 0) return prev;
      const [moved] = steps.splice(fromIdx, 1);
      steps.splice(targetIdx, 0, moved);
      const violation = orderViolation(steps);
      if (violation) {
        setDropHint(violation);
        window.setTimeout(() => setDropHint(null), 3500);
        return prev;
      }
      return { ...prev, steps };
    });
  }, []);

  const toggleLc = useCallback((lcId: string) => {
    setDraft((prev) => {
      const has = prev.allowedLcIds.includes(lcId);
      return {
        ...prev,
        allowedLcIds: has ? prev.allowedLcIds.filter((id) => id !== lcId) : [...prev.allowedLcIds, lcId],
        lcAgMatrix: has
          ? Object.fromEntries(Object.entries(prev.lcAgMatrix).filter(([k]) => k !== lcId))
          : prev.lcAgMatrix,
      };
    });
  }, []);

  const toggleMatrixCell = useCallback((lcId: string, agId: string) => {
    setDraft((prev) => {
      const row = prev.lcAgMatrix[lcId] ?? [];
      const next = row.includes(agId) ? row.filter((id) => id !== agId) : [...row, agId];
      return { ...prev, lcAgMatrix: { ...prev.lcAgMatrix, [lcId]: next } };
    });
  }, []);

  const toggleSubLine = useCallback((id: string) => {
    setDraft((prev) => ({
      ...prev,
      allowedSubLineIds: prev.allowedSubLineIds.includes(id)
        ? prev.allowedSubLineIds.filter((x) => x !== id)
        : [...prev.allowedSubLineIds, id],
    }));
  }, []);

  // ---------------------------------------------------------------------------
  // Freigeben: Prüfungen aus dem Konzept ("nicht freigebbar, was nicht
  // durchspielbar ist") + ein Rückgängig-Stand
  // ---------------------------------------------------------------------------
  const validateForActivation = useCallback((): string[] => {
    const errors: string[] = [];
    if (!draft.lineId) errors.push('Keine Strecke gewählt — der Flow hat keinen Einstieg.');

    const lc = stepRule('laundryClass');
    const ag = stepRule('articleGroup');
    const weight = stepRule('weight');

    if (!weight.enabled) errors.push('Ohne Gewichts-Schritt entsteht keine verwertbare Erfassung.');

    if (lc.enabled && draft.allowedLcIds.length === 0) {
      errors.push('Wäscheklasse ist im Flow, aber keine Klasse ist freigegeben — Sackgasse (vgl. Review H1).');
    }
    if (ag.enabled && !lc.enabled) {
      errors.push('Artikelgruppe ist im Flow, aber die Wäscheklasse nicht — die Zuordnung hängt in der Luft.');
    }
    if (ag.enabled && lc.enabled) {
      const empty = draft.allowedLcIds.filter((id) => (draft.lcAgMatrix[id] ?? []).length === 0);
      if (empty.length > 0) {
        const names = empty
          .map((id) => laundryClasses.find((x) => x.id === id)?.name ?? id)
          .join(', ');
        errors.push(`Wäscheklassen ohne Artikelgruppe (Sackgasse im Flow): ${names}`);
      }
    }
    for (const s of draft.steps) {
      if (!s.enabled && s.requirement !== 'OFF' && s.key !== 'subline') {
        errors.push(`„${STEP_META[s.key].label}" ist abgeschaltet, trägt aber eine Pflicht-Regel.`);
      }
      if (s.requiredIf && !draft.allowedLcIds.includes(s.requiredIf.valueId)) {
        errors.push(
          `Bedingte Pflicht auf „${STEP_META[s.key].label}" verweist auf eine nicht freigegebene Wäscheklasse.`,
        );
      }
      if (s.requiredIf && !lc.enabled) {
        errors.push(`Bedingte Pflicht auf „${STEP_META[s.key].label}" braucht den Wäscheklasse-Schritt im Flow.`);
      }
    }
    return errors;
  }, [draft, laundryClasses]); // eslint-disable-line react-hooks/exhaustive-deps

  const activate = useCallback(() => {
    const errors = validateForActivation();
    if (errors.length > 0) {
      setActivationErrors(errors);
      setActivationOk(false);
      return;
    }
    setPrevious(active);
    setActive({ flow: deepCopy(draft), activatedAt: new Date().toLocaleTimeString('de-DE') });
    setActivationErrors(null);
    setActivationOk(true);
    window.setTimeout(() => setActivationOk(false), 3000);
  }, [draft, active, validateForActivation]);

  const rollback = useCallback(() => {
    if (!previous) return;
    setActive(previous);
    setDraft(deepCopy(previous.flow));
    setPrevious(null);
  }, [previous]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Stammdaten...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Kopfzeile */}
        <div className="bg-white border-b px-6 py-4 flex flex-wrap items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Erfassungs-Flow Builder — Demo 4 (Folien-Muster)</h1>
            <p className="text-sm text-slate-500">
              Filmstreifen + Bühne + Probelauf · Entwurf wirkt erst nach Freigabe · nur lokaler State
            </p>
          </div>
          <div className="flex-1" />
          {/* Strecke wählen (Scope des Flows) */}
          <select
            value={draft.lineId ?? ''}
            onChange={(e) => setDraft((p) => ({ ...p, lineId: e.target.value || null, allowedSubLineIds: [] }))}
            className="border rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Strecke wählen…</option>
            {lines.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          {/* Statuschips */}
          <span className={`text-xs px-2 py-1 rounded-full border ${draftDirty ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
            Entwurf{draftDirty ? ' · geändert' : ' · wie aktiv'}
          </span>
          <span className={`text-xs px-2 py-1 rounded-full border ${active ? 'bg-green-50 border-green-300 text-green-800' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
            {active ? `Aktiv seit ${active.activatedAt}` : 'Noch nichts aktiv'}
          </span>
          {/* Aktionen */}
          <button
            onClick={() => setTrialOpen(true)}
            disabled={!draft.lineId}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-40 text-sm font-medium"
          >
            <Play className="w-4 h-4" /> Probelauf
          </button>
          <button
            onClick={activate}
            disabled={!draftDirty}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 text-sm font-medium"
          >
            <ShieldCheck className="w-4 h-4" /> Freigeben
          </button>
          <button
            onClick={rollback}
            disabled={!previous}
            title="Einen Stand zurück"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border text-slate-600 hover:bg-slate-50 disabled:opacity-40 text-sm"
          >
            <RotateCcw className="w-4 h-4" /> Rückgängig
          </button>
        </div>

        {/* Freigabe-Ergebnis */}
        {activationErrors && (
          <div className="mx-6 mt-4 rounded-lg border border-red-300 bg-red-50 p-4">
            <div className="flex items-center gap-2 font-medium text-red-800 mb-2">
              <AlertTriangle className="w-4 h-4" /> Nicht freigebbar — der Flow ist nicht durchspielbar:
            </div>
            <ul className="list-disc pl-6 text-sm text-red-700 space-y-1">
              {activationErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
            <button onClick={() => setActivationErrors(null)} className="mt-3 text-sm text-red-700 underline">
              Verstanden
            </button>
          </div>
        )}
        {activationOk && (
          <div className="mx-6 mt-4 rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-800 flex items-center gap-2">
            <Check className="w-4 h-4" /> Freigegeben — die Stationen würden ab jetzt diesen Stand laden.
            Der vorherige Stand liegt im Rückgängig-Slot.
          </div>
        )}

        {/* Drop-Hinweis (Reihenfolge-Sperre) */}
        {dropHint && (
          <div className="mx-6 mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {dropHint}
          </div>
        )}

        {/* Filmstreifen */}
        <div className="mx-6 mt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Filmstreifen — Reihenfolge per Ziehen, Auswahl per Klick
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {draft.steps.map((s, idx) => {
              const Meta = STEP_META[s.key];
              const enabledIdx = enabledSteps.findIndex((x) => x.key === s.key);
              const isSelected = selectedStep === s.key;
              return (
                <div
                  key={s.key}
                  draggable={s.key !== FIXED_FIRST}
                  onDragStart={(e: DragEvent) => { setDragKey(s.key); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={(e: DragEvent) => { e.preventDefault(); }}
                  onDrop={(e: DragEvent) => {
                    e.preventDefault();
                    if (dragKey && dragKey !== s.key) moveStep(dragKey, idx);
                    setDragKey(null);
                  }}
                  onClick={() => setSelectedStep(s.key)}
                  className={`min-w-[150px] cursor-pointer select-none rounded-lg border-2 p-3 bg-white transition-colors
                    ${isSelected ? 'border-blue-600 shadow-sm' : 'border-slate-200 hover:border-slate-300'}
                    ${!s.enabled ? 'opacity-45' : ''}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold
                      ${s.enabled ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                      {s.enabled ? enabledIdx + 1 : '–'}
                    </span>
                    <div className="flex items-center gap-1">
                      {s.requiredIf && <LinkIcon className="w-3.5 h-3.5 text-purple-600" />}
                      {s.requirement === 'HARD' && <span className="w-2 h-2 rounded-full bg-red-500" title="Pflicht (hart)" />}
                      {s.requirement === 'SOFT' && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Meta.icon className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900">{Meta.label}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 truncate">
                    {s.key === 'line' && (selectedLine ? `Vorausgewählt: ${selectedLine.name}` : 'keine Strecke')}
                    {s.key === 'subline' && `${draft.allowedSubLineIds.length} von ${subLines.length} erlaubt`}
                    {s.key === 'laundryClass' && `${draft.allowedLcIds.length} Klassen erlaubt`}
                    {s.key === 'articleGroup' && 'abhängig von Wäscheklasse'}
                    {s.key === 'customer' && (s.requiredIf ? 'Pflicht bei Bedingung' : 'Auswahl zur Laufzeit')}
                    {s.key === 'weight' && 'manuelle Eingabe (kg)'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bühne */}
        <div className="mx-6 my-4 grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
          <StageCard
            step={selectedStep}
            rule={stepRule(selectedStep)}
            draft={draft}
            lines={lines}
            subLines={subLines}
            laundryClasses={laundryClasses}
            articleGroups={articleGroups}
            customers={customers}
            onToggleLc={toggleLc}
            onToggleSubLine={toggleSubLine}
            onSelectLine={(id) => setDraft((prev) => ({ ...prev, lineId: id, allowedSubLineIds: [] }))}
            onOpenMatrix={() => setMatrixOpen(true)}
          />

          {/* Schritt-Regeln */}
          <div className="bg-white rounded-lg border shadow-sm h-fit">
            <div className="px-4 py-3 border-b font-semibold text-slate-900 flex items-center gap-2">
              Schritt-Regeln · {STEP_META[selectedStep].label}
            </div>
            <div className="p-4 space-y-4 text-sm">
              {/* An/Aus */}
              <label className={`flex items-center justify-between ${selectedStep === FIXED_FIRST ? 'opacity-40' : ''}`}>
                <span className="flex items-center gap-2"><Power className="w-4 h-4" /> Schritt im Flow</span>
                <input
                  type="checkbox"
                  checked={stepRule(selectedStep).enabled}
                  disabled={selectedStep === FIXED_FIRST}
                  onChange={(e) => patchStep(selectedStep, { enabled: e.target.checked })}
                  className="w-5 h-5"
                />
              </label>

              {/* Pflicht-Stufe */}
              <div>
                <div className="mb-1 text-slate-600">Pflicht</div>
                <div className="grid grid-cols-3 gap-2">
                  {(['OFF', 'SOFT', 'HARD'] as Requirement[]).map((r) => (
                    <button
                      key={r}
                      onClick={() => patchStep(selectedStep, { requirement: r })}
                      className={`px-2 py-2 rounded-lg border text-xs font-medium
                        ${stepRule(selectedStep).requirement === r
                          ? r === 'HARD' ? 'bg-red-600 text-white border-red-600'
                            : r === 'SOFT' ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-slate-600 text-white border-slate-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                    >
                      {r === 'OFF' ? 'Aus' : r === 'SOFT' ? 'Warnung' : 'Hart'}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Warnung = Modal mit Begründung, Speichern möglich („faul-sicher"). Hart = kein Weiter.
                </p>
              </div>

              {/* Bedingte Pflicht — nur für Kunde (Referenzfall #13) */}
              {selectedStep === 'customer' && (
                <div>
                  <div className="mb-1 text-slate-600 flex items-center gap-1">
                    <LinkIcon className="w-3.5 h-3.5 text-purple-600" /> Pflicht, wenn Wäscheklasse =
                  </div>
                  <select
                    value={stepRule('customer').requiredIf?.valueId ?? ''}
                    onChange={(e) =>
                      patchStep('customer', {
                        requiredIf: e.target.value
                          ? { stepKey: 'laundryClass', valueId: e.target.value }
                          : null,
                      })
                    }
                    className="w-full border rounded-lg px-2 py-2 bg-white"
                  >
                    <option value="">— keine Bedingung —</option>
                    {allowedLcs.map((lc) => (
                      <option key={lc.id} value={lc.id}>{lc.name}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Meldung #13: Bei Bewohnerwäsche wird der Kunde hart Pflicht, „ohne Kunde" entfällt.
                  </p>
                </div>
              )}

              {/* Matrix-Zugang */}
              {(selectedStep === 'laundryClass' || selectedStep === 'articleGroup') && (
                <button
                  onClick={() => setMatrixOpen(true)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50"
                >
                  <Grid3x3 className="w-4 h-4" /> Matrix Wäscheklasse × Artikelgruppe
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Matrix-Dialog */}
        {matrixOpen && (
          <MatrixDialog
            allowedLcs={allowedLcs}
            articleGroups={articleGroups}
            matrix={draft.lcAgMatrix}
            onToggle={toggleMatrixCell}
            onClose={() => setMatrixOpen(false)}
          />
        )}

        {/* Probelauf */}
        {trialOpen && (
          <TrialRun
            draft={draft}
            lines={lines}
            subLines={subLines}
            laundryClasses={laundryClasses}
            articleGroups={articleGroups}
            customers={customers}
            onClose={() => setTrialOpen(false)}
          />
        )}
      </div>
    </AdminLayout>
  );
}

// =============================================================================
// BÜHNE — der Schritt in Originalgröße, im Look des FlWizard
// =============================================================================

interface StageProps {
  step: StepKey;
  rule: StepRule;
  draft: FlowDraft;
  lines: ApiLine[];
  subLines: SubLine[];
  laundryClasses: LaundryClassEntity[];
  articleGroups: ArticleGroupEntity[];
  customers: Customer[];
  onToggleLc: (id: string) => void;
  onToggleSubLine: (id: string) => void;
  onSelectLine: (id: string) => void;
  onOpenMatrix: () => void;
}

function StageCard(p: StageProps) {
  const Meta = STEP_META[p.step];
  const allowedLcs = p.laundryClasses.filter((lc) => p.draft.allowedLcIds.includes(lc.id));

  return (
    <div className={`bg-white rounded-lg border shadow-sm ${!p.rule.enabled ? 'opacity-50' : ''}`}>
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Meta.icon className="w-5 h-5" />
          {p.step === 'line' && 'Strecke wählen'}
          {p.step === 'subline' && 'Substrecke wählen'}
          {p.step === 'laundryClass' && 'Wäscheklasse wählen'}
          {p.step === 'articleGroup' && 'Artikelgruppe wählen'}
          {p.step === 'customer' && 'Kunde wählen'}
          {p.step === 'weight' && 'Gewicht eingeben'}
        </h3>
        <span className="text-xs text-slate-400">so sieht es der Vorarbeiter · zum Bearbeiten klicken</span>
      </div>
      <div className="p-4">
        {!p.rule.enabled && (
          <div className="mb-3 text-sm text-slate-500 flex items-center gap-2">
            <Power className="w-4 h-4" /> Dieser Schritt ist abgeschaltet und erscheint nicht im Flow.
          </div>
        )}

        {p.step === 'line' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {p.lines.map((l) => (
              <button
                key={l.id}
                onClick={() => p.onSelectLine(l.id)}
                className={`min-h-[72px] text-lg border rounded-lg flex flex-col items-center justify-center gap-1 transition-colors
                  ${p.draft.lineId === l.id ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-200 text-slate-700 hover:bg-gray-50'}`}
              >
                <span className="flex items-center gap-2">
                  {p.draft.lineId === l.id && <Check className="w-4 h-4" />}
                  {l.name}
                </span>
                {l.subLines && l.subLines.length > 0 && (
                  <span className="text-xs text-slate-500">{l.subLines.length} Varianten</span>
                )}
              </button>
            ))}
            {p.lines.length === 0 && <EmptyHint text="Keine Strecken in den Stammdaten." />}
          </div>
        )}

        {p.step === 'subline' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {p.subLines.map((s) => {
              const on = p.draft.allowedSubLineIds.includes(s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => p.onToggleSubLine(s.id)}
                  className={`min-h-[72px] text-lg border rounded-lg flex items-center justify-center gap-2 transition-colors
                    ${on ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-dashed border-gray-300 text-slate-400 hover:bg-gray-50'}`}
                >
                  {on && <Check className="w-4 h-4" />} {s.name}
                </button>
              );
            })}
            {p.subLines.length === 0 && <EmptyHint text="Die gewählte Strecke hat keine Substrecken." />}
          </div>
        )}

        {p.step === 'laundryClass' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {p.laundryClasses.map((lc) => {
              const on = p.draft.allowedLcIds.includes(lc.id);
              return (
                <button
                  key={lc.id}
                  onClick={() => p.onToggleLc(lc.id)}
                  className={`min-h-[72px] text-lg border rounded-lg flex flex-col items-center justify-center gap-1 transition-colors
                    ${on ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-dashed border-gray-300 text-slate-400 hover:bg-gray-50'}`}
                >
                  <span className="flex items-center gap-2">{on && <Check className="w-4 h-4" />}{lc.name}</span>
                  <span className="text-xs text-slate-400">{lc.code}</span>
                </button>
              );
            })}
            {p.laundryClasses.length === 0 && <EmptyHint text="Keine Wäscheklassen in den Stammdaten." />}
          </div>
        )}

        {p.step === 'articleGroup' && (
          <div>
            {allowedLcs.length === 0 ? (
              <EmptyHint text="Erst Wäscheklassen freigeben — Artikelgruppen hängen daran." />
            ) : (
              <div className="space-y-3">
                {allowedLcs.map((lc) => {
                  const ags = (p.draft.lcAgMatrix[lc.id] ?? [])
                    .map((id) => p.articleGroups.find((a) => a.id === id))
                    .filter(Boolean) as ArticleGroupEntity[];
                  return (
                    <div key={lc.id} className="border rounded-lg p-3">
                      <div className="text-sm font-medium text-slate-700 mb-2">{lc.name}</div>
                      <div className="flex flex-wrap gap-2">
                        {ags.map((ag) => (
                          <span key={ag.id} className="px-3 py-2 rounded-lg border border-gray-200 text-slate-700">
                            {ag.name}
                          </span>
                        ))}
                        {ags.length === 0 && (
                          <span className="text-sm text-red-600 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" /> keine Artikelgruppe — Sackgasse
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                <button onClick={p.onOpenMatrix} className="text-sm text-blue-700 underline flex items-center gap-1">
                  <Grid3x3 className="w-4 h-4" /> Zuordnung in der Matrix bearbeiten
                </button>
              </div>
            )}
          </div>
        )}

        {p.step === 'customer' && (
          <div>
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2 text-slate-400 mb-3">
              <Search className="w-4 h-4" /> Suche (zur Laufzeit)
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {p.customers.slice(0, 8).map((c) => (
                <div key={c.id} className="min-h-[72px] border border-gray-200 rounded-lg flex items-center justify-center text-center px-2 text-slate-700">
                  {c.shortName || c.name}
                </div>
              ))}
              {p.customers.length === 0 && <EmptyHint text="Keine Kunden in den Stammdaten." />}
            </div>
          </div>
        )}

        {p.step === 'weight' && <NumpadPreview />}
      </div>
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <div className="col-span-full text-sm text-slate-400 py-6 text-center">{text}</div>;
}

/** Numpad im FlWizard-Layout (Anzeige; im Probelauf funktional) */
function NumpadPreview({ onInput, value }: { onInput?: (key: string) => void; value?: string }) {
  const keys = ['7', '8', '9', '4', '5', '6', '1', '2', '3', ',', '0', '⌫'];
  return (
    <div className="max-w-sm">
      <div className="border rounded-lg px-4 py-3 text-right text-3xl font-mono mb-3 min-h-[56px] bg-gray-50">
        {value || <span className="text-slate-300">0</span>}
        <span className="text-base text-slate-400 ml-2">kg</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {keys.map((k) => (
          <button
            key={k}
            onClick={() => onInput?.(k)}
            className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100"
          >
            {k}
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// MATRIX — Wäscheklasse × Artikelgruppe
// =============================================================================

function MatrixDialog(p: {
  allowedLcs: LaundryClassEntity[];
  articleGroups: ArticleGroupEntity[];
  matrix: Record<string, string[]>;
  onToggle: (lcId: string, agId: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Grid3x3 className="w-5 h-5" /> Matrix — welche Artikelgruppen je Wäscheklasse
          </h3>
          <button onClick={p.onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-auto">
          {p.allowedLcs.length === 0 ? (
            <div className="text-sm text-slate-500">Erst Wäscheklassen freigeben (Bühne „Wäscheklasse").</div>
          ) : (
            <table className="text-sm border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2 border-b font-medium text-slate-500">Wäscheklasse ↓ · Artikelgruppe →</th>
                  {p.articleGroups.map((ag) => (
                    <th key={ag.id} className="p-2 border-b font-medium text-slate-700 whitespace-nowrap">{ag.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {p.allowedLcs.map((lc) => {
                  const row = p.matrix[lc.id] ?? [];
                  const empty = row.length === 0;
                  return (
                    <tr key={lc.id} className={empty ? 'bg-red-50' : ''}>
                      <td className="p-2 border-b font-medium text-slate-800 whitespace-nowrap">
                        {lc.name}
                        {empty && (
                          <span className="ml-2 text-xs text-red-600 inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> leer
                          </span>
                        )}
                      </td>
                      {p.articleGroups.map((ag) => (
                        <td key={ag.id} className="p-2 border-b text-center">
                          <input
                            type="checkbox"
                            className="w-5 h-5"
                            checked={row.includes(ag.id)}
                            onChange={() => p.onToggle(lc.id, ag.id)}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-4 py-3 border-t text-xs text-slate-500">
          Leere Zeilen sind Sackgassen: Der Vorarbeiter käme dort nicht weiter — Freigeben wird blockiert.
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// PROBELAUF — der Flow, wie ihn der Vorarbeiter erlebt (mit echtem Stepper)
// =============================================================================

function TrialRun(p: {
  draft: FlowDraft;
  lines: ApiLine[];
  subLines: SubLine[];
  laundryClasses: LaundryClassEntity[];
  articleGroups: ArticleGroupEntity[];
  customers: Customer[];
  onClose: () => void;
}) {
  const steps = p.draft.steps.filter((s) => s.enabled && (s.key !== 'subline' || p.draft.allowedSubLineIds.length > 0));
  const [idx, setIdx] = useState(0);
  const [choices, setChoices] = useState<Partial<Record<StepKey, string>>>({ line: p.draft.lineId ?? undefined });
  const [weightStr, setWeightStr] = useState('');
  const [softModal, setSoftModal] = useState<StepKey | null>(null);
  const [done, setDone] = useState(false);

  const current = steps[idx];
  const stepLabels = steps.map((s) => STEP_META[s.key].label);

  /** Effektive Pflicht: HARD, wenn requiredIf durch die LC-Wahl ausgelöst wird (#13) */
  const effectiveRequirement = (rule: StepRule): Requirement => {
    if (rule.requiredIf && choices.laundryClass === rule.requiredIf.valueId) return 'HARD';
    return rule.requirement;
  };

  const valueFor = (key: StepKey): string | undefined =>
    key === 'weight' ? (weightStr || undefined) : choices[key];

  const tryNext = () => {
    if (!current) return;
    const req = effectiveRequirement(current);
    const has = !!valueFor(current.key);
    if (!has && req === 'HARD') return; // Button ist ohnehin gesperrt — doppelte Sicherung
    if (!has && req === 'SOFT') { setSoftModal(current.key); return; }
    advance();
  };

  const advance = () => {
    setSoftModal(null);
    if (idx + 1 >= steps.length) setDone(true);
    else setIdx(idx + 1);
  };

  const numpad = (k: string) => {
    if (k === '⌫') setWeightStr((w) => w.slice(0, -1));
    else if (k === ',') setWeightStr((w) => (w.includes(',') ? w : w + ','));
    else setWeightStr((w) => (w.length < 6 ? w + k : w));
  };

  const currentReq = current ? effectiveRequirement(current) : 'OFF';
  const currentHas = current ? !!valueFor(current.key) : false;
  const forcedByCondition = current?.requiredIf && choices.laundryClass === current.requiredIf.valueId;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-slate-50 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 bg-white border-b rounded-t-lg flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Play className="w-4 h-4 text-blue-600" /> Probelauf — so erlebt es der Vorarbeiter
          </h3>
          <button onClick={p.onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>

        {/* Der echte Stepper aus der Produktiv-UI */}
        <Stepper steps={stepLabels} currentStep={done ? steps.length : idx} />

        <div className="p-4 overflow-auto flex-1">
          {done ? (
            <div className="text-center py-10">
              <Check className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <div className="text-lg font-semibold text-slate-900">Probelauf abgeschlossen</div>
              <p className="text-sm text-slate-500 mt-1">
                {steps.length} Schritte durchspielt — der Flow ist durchgängig begehbar.
              </p>
            </div>
          ) : current ? (
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900">
                  {STEP_META[current.key].label} wählen
                </h4>
                {forcedByCondition && (
                  <span className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-1 flex items-center gap-1">
                    <LinkIcon className="w-3 h-3" /> Pflicht wegen Wäscheklassen-Wahl
                  </span>
                )}
              </div>
              <div className="p-4">
                {current.key === 'line' && (
                  <TrialGrid
                    items={p.lines.filter((l) => l.id === p.draft.lineId).map((l) => ({ id: l.id, label: l.name }))}
                    chosen={choices.line}
                    onPick={(id) => setChoices((c) => ({ ...c, line: id }))}
                  />
                )}
                {current.key === 'subline' && (
                  <TrialGrid
                    items={p.subLines.filter((s) => p.draft.allowedSubLineIds.includes(s.id)).map((s) => ({ id: s.id, label: s.name }))}
                    chosen={choices.subline}
                    onPick={(id) => setChoices((c) => ({ ...c, subline: id }))}
                  />
                )}
                {current.key === 'laundryClass' && (
                  <TrialGrid
                    items={p.laundryClasses.filter((lc) => p.draft.allowedLcIds.includes(lc.id)).map((lc) => ({ id: lc.id, label: lc.name }))}
                    chosen={choices.laundryClass}
                    onPick={(id) => setChoices((c) => ({ ...c, laundryClass: id, articleGroup: undefined }))}
                  />
                )}
                {current.key === 'articleGroup' && (
                  <TrialGrid
                    items={(p.draft.lcAgMatrix[choices.laundryClass ?? ''] ?? [])
                      .map((id) => p.articleGroups.find((a) => a.id === id))
                      .filter(Boolean)
                      .map((a) => ({ id: a!.id, label: a!.name }))}
                    chosen={choices.articleGroup}
                    onPick={(id) => setChoices((c) => ({ ...c, articleGroup: id }))}
                    emptyText="Für die gewählte Wäscheklasse sind keine Artikelgruppen zugeordnet (Sackgasse!)."
                  />
                )}
                {current.key === 'customer' && (
                  <TrialGrid
                    items={p.customers.slice(0, 8).map((c) => ({ id: c.id, label: c.shortName || c.name }))}
                    chosen={choices.customer}
                    onPick={(id) => setChoices((c) => ({ ...c, customer: id }))}
                  />
                )}
                {current.key === 'weight' && <NumpadPreview onInput={numpad} value={weightStr} />}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">Der Flow hat keine aktiven Schritte.</div>
          )}
        </div>

        {!done && current && (
          <div className="px-4 py-3 bg-white border-t rounded-b-lg flex items-center justify-between">
            <button
              onClick={() => setIdx(Math.max(0, idx - 1))}
              disabled={idx === 0}
              className="flex items-center gap-1 px-3 py-2 rounded-lg border text-slate-600 disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" /> Zurück
            </button>
            <div className="text-xs text-slate-400">
              {currentReq === 'HARD' && !currentHas && 'Pflichtschritt — ohne Auswahl geht es nicht weiter.'}
              {currentReq === 'SOFT' && !currentHas && 'Optional mit Warnung — Weiter zeigt ein Begründungs-Modal.'}
            </div>
            <button
              onClick={tryNext}
              disabled={currentReq === 'HARD' && !currentHas}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-40"
            >
              {idx + 1 >= steps.length ? 'Erfassung abschliessen' : 'Weiter'}
            </button>
          </div>
        )}
        {done && (
          <div className="px-4 py-3 bg-white border-t rounded-b-lg text-right">
            <button onClick={p.onClose} className="px-4 py-2 rounded-lg bg-blue-600 text-white">Schliessen</button>
          </div>
        )}

        {/* Soft-Warnmodal — das "faul-sicher"-Muster aus der Produktiv-UI */}
        {softModal && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-lg">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 m-4">
              <div className="flex items-center gap-2 text-amber-700 font-semibold mb-2">
                <AlertTriangle className="w-5 h-5" /> {STEP_META[softModal].label} nicht angegeben
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Der Schritt ist als „Warnung" konfiguriert: Speichern ist möglich, wird aber bewusst bestätigt.
              </p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setSoftModal(null)} className="px-3 py-2 rounded-lg border text-slate-700">
                  Zurück zur Auswahl
                </button>
                <button onClick={advance} className="px-3 py-2 rounded-lg bg-amber-600 text-white">
                  Trotzdem weiter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TrialGrid(p: {
  items: { id: string; label: string }[];
  chosen?: string;
  onPick: (id: string) => void;
  emptyText?: string;
}) {
  if (p.items.length === 0) {
    return (
      <div className="text-sm text-red-600 flex items-center gap-2 py-6 justify-center">
        <AlertTriangle className="w-4 h-4" /> {p.emptyText ?? 'Keine Auswahl verfügbar.'}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {p.items.map((it) => (
        <button
          key={it.id}
          onClick={() => p.onPick(it.id)}
          className={`min-h-[72px] text-lg border rounded-lg transition-colors flex items-center justify-center gap-2
            ${p.chosen === it.id ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-200 hover:bg-gray-50 text-slate-700'}`}
        >
          {p.chosen === it.id && <Check className="w-4 h-4" />} {it.label}
        </button>
      ))}
    </div>
  );
}
